import React, { useEffect, useState } from 'react';
import ClearIcon from '@mui/icons-material/Close';
import { Button, Chip, Divider, Paper } from '@mui/material';

import { TooltipIconButton, useElementRect } from '@paragon/client-tools';
import { OptionalTooltip } from '@paragon/material';
import { pluralize } from '@paragon/utilities';

import { CHECKED_ROWS_ACTION_BAR_BOTTOM } from '../layoutConstants';
import { showArchiveConfirmationDialog, showMoveCopyDestinationDialog } from '../store/actions/dialogActions';
import { requestRepair, requestSeals } from '../store/actions/projects';
import { getScopeProjectGuids } from '../store/projectActionScope';
import { getCheckedProjectGuids, getCheckedRowsScope } from '../store/selectors/grid';
import { getProjectsByGuid } from '../store/selectors/layoutModels';
import { actionBarRectSet } from '../store/slices/applicationSlice';
import { checkedRowsCleared } from '../store/slices/gridSlice';
import { useAppDispatch, useAppSelector } from './hooks';
import { DownloadButtons } from './Tools/DownloadButtons';
import { ProductionButtons } from './Tools/ProductionButtons';

export function CheckedRowsActionBar() {
  const dispatch = useAppDispatch();

  const checkedProjectGuids = useAppSelector(getCheckedProjectGuids);
  const checkedProjectCount = checkedProjectGuids.length;

  const scope = useAppSelector(getCheckedRowsScope);
  const checkedComponentCount = scope.componentDesignPaths.length;

  const projectsByGuid = useAppSelector(getProjectsByGuid);
  const regionNotSet = getScopeProjectGuids(scope).some((guid) => !projectsByGuid[guid]?.region);

  const [paperEl, setPaperEl] = useState<HTMLDivElement | null>(null);
  const paperRect = useElementRect(paperEl);

  const barLeft = paperRect ? Math.round(paperRect.left) : null;
  const barHeight = paperRect ? Math.round(paperRect.height) : null;

  useEffect(() => {
    dispatch(actionBarRectSet(barLeft !== null && barHeight !== null ? { left: barLeft, height: barHeight } : null));
  }, [dispatch, barLeft, barHeight]);

  useEffect(() => () => void dispatch(actionBarRectSet(null)), [dispatch]);

  if (checkedProjectCount === 0 && checkedComponentCount === 0) return null;

  const countLabel = [
    checkedProjectCount > 0 ? `${checkedProjectCount} ${pluralize('project', checkedProjectCount)}` : null,
    checkedComponentCount > 0 ? `${checkedComponentCount} ${pluralize('component', checkedComponentCount)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Paper
      ref={setPaperEl}
      elevation={8}
      sx={{
        position: 'absolute',
        bottom: CHECKED_ROWS_ACTION_BAR_BOTTOM,
        left: 0,
        right: 0,
        mx: 'auto',
        width: 'fit-content',
        maxWidth: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        padding: 1,
        whiteSpace: 'nowrap',
        '& .MuiButton-text:not(.Mui-disabled)': { color: 'text.primary' },
      }}
    >
      <Chip label={countLabel} color="primary" />
      <TooltipIconButton title="Uncheck all" size="small" onClick={() => dispatch(checkedRowsCleared())}>
        <ClearIcon fontSize="small" />
      </TooltipIconButton>

      {checkedProjectCount > 0 && (
        <>
          <Divider orientation="vertical" flexItem />
          <Button size="small" onClick={() => dispatch(showArchiveConfirmationDialog(checkedProjectGuids))}>
            Archive
          </Button>
        </>
      )}

      {checkedComponentCount > 0 && (
        <>
          <Divider orientation="vertical" flexItem />
          <Button size="small" onClick={() => dispatch(showMoveCopyDestinationDialog('copy', scope))}>
            Copy
          </Button>
          <Button size="small" onClick={() => dispatch(showMoveCopyDestinationDialog('move', scope))}>
            Move
          </Button>
          <DownloadButtons scope={scope} />
          <OptionalTooltip title="Project must have a region set" visible={regionNotSet}>
            <Button size="small" disabled={regionNotSet} onClick={() => dispatch(requestSeals(scope))}>
              Request Seals
            </Button>
          </OptionalTooltip>
          <OptionalTooltip title="Project must have a region set" visible={regionNotSet}>
            <Button size="small" disabled={regionNotSet} onClick={() => dispatch(requestRepair(scope))}>
              Request Repair
            </Button>
          </OptionalTooltip>
          <ProductionButtons scope={scope} />
        </>
      )}
    </Paper>
  );
}
